/**
 * Voice Assistant Page
 * Press mic to speak, tap suggestions for follow-ups.
 * Uses same markdown/table/chart rendering as client chat.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Loader2, Sparkles, User, Copy, Check, CalendarPlus, ArrowDown, Send, X,
  Volume2, VolumeX, Mic, Sun, ListTodo, BarChart3, History, Plus,
} from 'lucide-react';
import { useOutletContext, useNavigate, useLocation, Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { FormattedChatMessage } from '@/components/chat/ChatRenderer';
import apiClient from '@/services/api/ma-client';
import { useManager } from '@/services/api/manager-auth';
import { TypingIndicator } from '@/components/pwa/animations';
import { pa } from './base';
import { useActiveClient } from './active-client';
import ChatHistoryPanel from './ChatHistoryPanel';
import { archiveChat, ArchivedChat } from './chat-history';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestions?: string[];
  // Action confirmation card (assistant asked the manager to approve an action)
  confirm?: { action: string; params: any };
  cardState?: 'pending' | 'sent' | 'cancelled';
  // Inline success chip for completed actions
  actionDone?: string;
}

const LANG_LABELS: Record<string, string> = { en: 'English', fa: 'Persian', ar: 'Arabic', ko: 'Korean' };
const RTL_LANGS = ['ar', 'fa'];

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    const plain = typeof text === 'string' ? text : String(text);
    navigator.clipboard.writeText(plain).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
  };
  return (
    <button onClick={handleCopy} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition" title="Copy">
      {copied ? <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

// Confirmation card shown for "confirm" responses (send message / draft email)
function ConfirmCard({ confirm, state, onConfirm, onCancel }: {
  confirm: { action: string; params: any };
  state: 'pending' | 'sent' | 'cancelled';
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const { action, params } = confirm;

  if (state === 'cancelled') {
    return (
      <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <X className="h-3.5 w-3.5" /> Cancelled
      </div>
    );
  }
  if (state === 'sent') return null; // content already replaced the card

  if (action === 'send_client_message') {
    return (
      <div className="mt-2 bg-card border border-border rounded-xl p-3">
        <p className="text-xs font-medium text-foreground mb-2">Send to {params?.clientName || 'client'}</p>
        <div className="bg-muted/50 rounded-lg p-2 text-sm text-foreground whitespace-pre-wrap mb-3">{params?.body}</div>
        <div className="flex items-center gap-2">
          <button onClick={onConfirm} className="inline-flex items-center gap-1.5 bg-[var(--v4-accent)] text-white rounded-lg px-3 py-1.5 text-sm font-medium active:scale-95 transition">
            <Send className="h-3.5 w-3.5" /> Send
          </button>
          <button onClick={onCancel} className="inline-flex items-center gap-1.5 bg-muted text-foreground rounded-lg px-3 py-1.5 text-sm font-medium active:scale-95 transition">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (action === 'draft_email') {
    const copyEmail = () => {
      const plain = `${params?.subject || ''}\n\n${params?.body || ''}`;
      navigator.clipboard.writeText(plain).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
    };
    return (
      <div className="mt-2 bg-card border border-border rounded-xl p-3">
        {params?.to && <p className="text-xs text-muted-foreground mb-1">To: {params.to}</p>}
        <p className="text-sm font-medium text-foreground mb-2">{params?.subject}</p>
        <div className="bg-muted/50 rounded-lg p-2 text-sm text-foreground whitespace-pre-wrap mb-3">{params?.body}</div>
        <div className="flex items-center gap-2">
          <button onClick={copyEmail} className="inline-flex items-center gap-1.5 bg-muted text-foreground rounded-lg px-3 py-1.5 text-sm font-medium active:scale-95 transition">
            {copied ? <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" /> : <Copy className="h-3.5 w-3.5" />} Copy
          </button>
          <button onClick={onConfirm} className="inline-flex items-center gap-1.5 bg-[var(--v4-accent)] text-white rounded-lg px-3 py-1.5 text-sm font-medium active:scale-95 transition">
            <Check className="h-3.5 w-3.5" /> Approve
          </button>
          <button onClick={onCancel} className="inline-flex items-center gap-1.5 bg-muted text-foreground rounded-lg px-3 py-1.5 text-sm font-medium active:scale-95 transition">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Fallback for unknown confirm actions: generic approve/cancel
  return (
    <div className="mt-2 bg-card border border-border rounded-xl p-3">
      <div className="flex items-center gap-2">
        <button onClick={onConfirm} className="inline-flex items-center gap-1.5 bg-[var(--v4-accent)] text-white rounded-lg px-3 py-1.5 text-sm font-medium active:scale-95 transition">
          <Check className="h-3.5 w-3.5" /> Confirm
        </button>
        <button onClick={onCancel} className="inline-flex items-center gap-1.5 bg-muted text-foreground rounded-lg px-3 py-1.5 text-sm font-medium active:scale-95 transition">
          Cancel
        </button>
      </div>
    </div>
  );
}

// Friendly label for a completed/confirmed action
function actionLabel(action?: string): string {
  switch (action) {
    case 'create_task': return 'Task created';
    case 'book_event':
    case 'create_event': return 'Event booked';
    case 'send_client_message': return 'Message sent';
    case 'draft_email': return 'Email drafted';
    default: return action ? action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Done';
  }
}

// Strip chart blocks + markdown so device TTS reads clean prose
function sanitizeForSpeech(raw: string): string {
  return String(raw || '')
    .replace(/\[CHART\][\s\S]*?\[\/CHART\]/gi, ' ') // remove chart blocks entirely
    .replace(/```[\s\S]*?```/g, ' ')                 // fenced code
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')       // links/images -> label text
    .replace(/[*#`_~>]/g, ' ')                        // markdown emphasis/heading marks
    .replace(/\|/g, ' ')                              // table pipes
    .replace(/\s+/g, ' ')                             // collapse whitespace
    .trim();
}

// Detect calendar-related voice commands
const CALENDAR_PATTERNS = /\b(schedule|book|add.+(?:event|meeting|appointment|call)|create.+(?:event|meeting|appointment)|set.+(?:meeting|reminder|appointment)|put.+(?:in|on).+calendar)\b/i;

function parseVoiceToEvent(text: string): { title: string; date: string; startTime: string; endTime: string } | null {
  const today = new Date();
  let date = today.toISOString().split('T')[0];
  let startTime = '09:00';
  let endTime = '10:00';

  if (/tomorrow/i.test(text)) { const d = new Date(today); d.setDate(d.getDate() + 1); date = d.toISOString().split('T')[0]; }
  const dayMatch = text.match(/next\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i);
  if (dayMatch) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const target = days.indexOf(dayMatch[1].toLowerCase());
    const d = new Date(today);
    const diff = (target - d.getDay() + 7) % 7 || 7;
    d.setDate(d.getDate() + diff);
    date = d.toISOString().split('T')[0];
  }
  const timeMatch = text.match(/at\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
  if (timeMatch) {
    let h = parseInt(timeMatch[1]);
    const m = timeMatch[2] || '00';
    if (timeMatch[3]?.toLowerCase() === 'pm' && h < 12) h += 12;
    if (timeMatch[3]?.toLowerCase() === 'am' && h === 12) h = 0;
    startTime = `${h.toString().padStart(2, '0')}:${m}`;
    endTime = `${(h + 1).toString().padStart(2, '0')}:${m}`;
  }
  let title = text.replace(/tomorrow/i, '').replace(/next\s+\w+/i, '').replace(/at\s+\d{1,2}(?::\d{2})?\s*(?:am|pm)?/i, '')
    .replace(/\b(schedule|book|add|create|set|put)\b/gi, '').replace(/\b(an?|the|event|meeting|appointment|call|in|on|my|calendar|for|to|with)\b/gi, '')
    .replace(/\s+/g, ' ').trim();
  if (!title) return null;
  title = title.charAt(0).toUpperCase() + title.slice(1);
  return { title, date, startTime, endTime };
}

export default function VoicePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const context = useOutletContext<{ language?: string }>();
  const language = context?.language || localStorage.getItem('pa-language') || 'en';
  const manager = useManager();
  const { activeClient } = useActiveClient();
  const uid = manager?.id || manager?.email || 'default';
  // Chat history is per active client - selecting a client shows that client's
  // chat; no active client = the general firm chat.
  const chatKey = activeClient ? `pa-chat-history-${uid}-c-${activeClient.id}` : `pa-chat-history-${uid}`;
  const [historyOpen, setHistoryOpen] = useState(false);

  // Clean up old shared key from before per-user storage
  useEffect(() => { localStorage.removeItem('pa-chat-history'); }, []);

  const [isProcessing, setIsProcessing] = useState(false);
  const [messages, setMessages] = useState<Message[]>(() => {
    try {
      const saved = localStorage.getItem(chatKey);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [error, setError] = useState('');
  const [showScrollPill, setShowScrollPill] = useState(false);
  const [speakingIndex, setSpeakingIndex] = useState<number | null>(null);
  const [listening, setListening] = useState(false);
  const [voiceText, setVoiceText] = useState('');
  const recognitionRef = useRef<any>(null);
  const transcriptRef = useRef('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Keep a live ref to messages so history is fresh inside event-listener closures
  const messagesRef = useRef<Message[]>(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  // The storage key the current `messages` belong to (so persistence and the
  // client-switch never write one client's chat into another's slot).
  const loadedKeyRef = useRef(chatKey);
  // Always-latest processQuery so the pending-drain and event effects never call
  // a stale closure (kept fresh by a plain assignment after processQuery below).
  const processQueryRef = useRef<(q: string) => void>(() => {});

  // Persist the current chat to the key it belongs to (loadedKeyRef), not the
  // freshly-computed chatKey, to avoid writing the old chat into the new client's
  // slot during a switch.
  useEffect(() => {
    try {
      if (messages.length > 0) localStorage.setItem(loadedKeyRef.current, JSON.stringify(messages.slice(-50)));
    } catch { /* ignore */ }
  }, [messages]);

  // When the active client changes: save the current chat, load the selected
  // client's chat (empty = a fresh chat to start), and jump to the latest message.
  useEffect(() => {
    if (loadedKeyRef.current === chatKey) return;
    try {
      const cur = messagesRef.current;
      if (cur.length > 0) localStorage.setItem(loadedKeyRef.current, JSON.stringify(cur.slice(-50)));
    } catch { /* ignore */ }
    let next: Message[] = [];
    try { const raw = localStorage.getItem(chatKey); next = raw ? JSON.parse(raw) : []; } catch { next = []; }
    loadedKeyRef.current = chatKey;
    setError('');
    setMessages(next);
    const jump = () => { const c = scrollContainerRef.current; if (c) c.scrollTop = c.scrollHeight; };
    setTimeout(jump, 80);
    setTimeout(jump, 280);
  }, [chatKey]);

  // Auto-scroll only if near bottom, otherwise show pill
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); return; }
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
    if (isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setShowScrollPill(false);
    } else if (messages.length > 0 && messages[messages.length - 1]?.role === 'assistant') {
      setShowScrollPill(true);
    }
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowScrollPill(false);
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
    if (isNearBottom) setShowScrollPill(false);
  }, []);

  // When the chat opens (mount or navigation here), jump straight to the latest
  // message. Two passes catch async-rendered content (markdown/charts) growing
  // the scroll height after the first paint.
  useEffect(() => {
    const jump = () => { const c = scrollContainerRef.current; if (c) c.scrollTop = c.scrollHeight; };
    const t1 = setTimeout(jump, 60);
    const t2 = setTimeout(jump, 250);
    setShowScrollPill(false);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [location.key]);

  // Drain a query handed over by the layout input bar via sessionStorage. Keyed
  // on location so it also fires when navigating /-> /voice reuses this component
  // WITHOUT remounting (the old mount-only effect silently dropped that query -
  // the "first message doesn't appear until I send it again" bug).
  useEffect(() => {
    const pending = sessionStorage.getItem('pa-pending-query');
    if (pending) {
      sessionStorage.removeItem('pa-pending-query');
      processQueryRef.current(pending);
    }
  }, [location.key]);

  // Queries submitted while already on the chat page arrive via a window event.
  useEffect(() => {
    const handler = (e: Event) => {
      const query = (e as CustomEvent).detail;
      if (query) processQueryRef.current(query);
    };
    window.addEventListener('pa-query', handler);
    return () => window.removeEventListener('pa-query', handler);
  }, []);

  const processQuery = async (query: string) => {
    // Detect calendar commands and redirect to calendar page with parsed event
    if (CALENDAR_PATTERNS.test(query)) {
      const parsed = parseVoiceToEvent(query);
      if (parsed) {
        setMessages(prev => [...prev, { role: 'user', content: query, timestamp: new Date() }]);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `I'll add that to your calendar:\n\n**${parsed.title}**\nDate: ${parsed.date}\nTime: ${parsed.startTime} - ${parsed.endTime}\n\nRedirecting to calendar for confirmation...`,
          timestamp: new Date(),
        }]);
        // Store pending event and navigate to calendar
        sessionStorage.setItem('pa-pending-calendar-event', JSON.stringify(parsed));
        setTimeout(() => navigate(pa('/calendar')), 1500);
        return;
      }
    }

    setIsProcessing(true);
    setMessages(prev => [...prev, { role: 'user', content: query, timestamp: new Date() }]);

    try {
      // Build last 6 messages of history for the assistant context
      const history = messagesRef.current.slice(-6).map(m => ({ role: m.role, content: m.content }));
      const paResp = await apiClient.post('/pa/assistant', {
        message: query,
        history,
        clientId: activeClient?.id,
        clientName: activeClient?.name,
      });
      const data = paResp.data?.data ?? paResp.data;
      const type = data?.type;

      if (type === 'defer') {
        // Info question: fall back to the rich /multi-client/query flow
        await runInfoQuery(query);
      } else if (type === 'action_done') {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.content || 'Done.',
          timestamp: new Date(),
          actionDone: actionLabel(data.action),
          suggestions: data.followups?.length ? data.followups : undefined,
        }]);
      } else if (type === 'confirm') {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.content || '',
          timestamp: new Date(),
          confirm: { action: data.action, params: data.params },
          cardState: 'pending',
        }]);
      } else {
        // "answer" or any other type: plain assistant message
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data?.content || (data ? JSON.stringify(data) : 'No response received.'),
          timestamp: new Date(),
          suggestions: data?.followups?.length ? data.followups : undefined,
        }]);
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const timedOut = status === 504 || status === 502 || err?.code === 'ECONNABORTED';
      const errMsg = timedOut
        ? 'That one took too long to come back. Try asking for a specific client or a shorter range (for example "overdue deadlines this month").'
        : (err?.response?.data?.error || err?.message || 'Failed to process your question.');
      setMessages(prev => [...prev, { role: 'assistant', content: String(errMsg), timestamp: new Date() }]);
    } finally {
      setIsProcessing(false);
    }
  };
  // Runs during render (before effects), so the drain/event effects always call
  // the current processQuery - no stale activeClient / messages closures.
  processQueryRef.current = processQuery;

  // Existing rich financial Q&A flow (used for the "defer" path)
  const runInfoQuery = async (query: string) => {
    const response = await apiClient.post('/multi-client/query', {
      message: `IMPORTANT: Keep your response brief and concise. Use short bullet points, small tables, and direct answers. This is for a busy manager on mobile. No lengthy explanations.\nIMPORTANT: Search ALL data sources: Xero financial data (P&L, Balance Sheet), uploaded client documents (tax returns, financial statements, receipts), and ATO knowledge base. Always check documents and Xero data together.${language !== 'en' ? `\n\nIMPORTANT: Respond in ${LANG_LABELS[language] || language}. Translate all text including table headers and labels.` : ''}\n\nQuestion: ${query}`,
    });

    const d = response.data?.data;
    let answer = '';
    if (typeof d === 'string') answer = d;
    else if (d?.message?.content && typeof d.message.content === 'string') answer = d.message.content;
    else if (d?.response && typeof d.response === 'string') answer = d.response;
    else if (d?.message && typeof d.message === 'string') answer = d.message;
    else answer = d ? JSON.stringify(d) : 'No response received.';

    // Generate follow-up suggestions based on the query
    const suggestions = generateSuggestions(query, answer);

    setMessages(prev => [...prev, { role: 'assistant', content: answer, timestamp: new Date(), suggestions }]);
  };

  // Execute a confirmed action; replaces the card with the returned content
  const executeAction = async (msgIndex: number, action: string, params: any) => {
    try {
      const r = await apiClient.post('/pa/assistant/execute', { action, params });
      const result = r.data?.data ?? r.data;
      const content = result?.content || 'Done.';
      setMessages(prev => prev.map((m, i) => i === msgIndex
        ? { ...m, content, cardState: 'sent', actionDone: actionLabel(action) }
        : m));
    } catch (err: any) {
      const errMsg = err?.response?.data?.error || err?.message || 'Failed to complete the action.';
      setMessages(prev => prev.map((m, i) => i === msgIndex
        ? { ...m, content: String(errMsg), cardState: 'cancelled' }
        : m));
    }
  };

  const dismissCard = (msgIndex: number) => {
    setMessages(prev => prev.map((m, i) => i === msgIndex ? { ...m, cardState: 'cancelled' } : m));
  };

  // Start a fresh chat, archiving the current thread so it stays in history.
  const newChat = () => {
    archiveChat(uid, messagesRef.current);
    setMessages([]);
    localStorage.removeItem(chatKey);
    setHistoryOpen(false);
  };

  // Reopen a past conversation (archiving the current one first, if any).
  const restoreChat = (chat: ArchivedChat) => {
    archiveChat(uid, messagesRef.current);
    setMessages(Array.isArray(chat.messages) ? chat.messages : []);
    setHistoryOpen(false);
  };

  // Open history when navigated here from the menu's "Chat history" link.
  useEffect(() => {
    if ((location.state as any)?.openHistory) {
      setHistoryOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  // Read an assistant message aloud via device TTS (or stop if already speaking it)
  const toggleSpeak = useCallback((index: number, content: string) => {
    if (!('speechSynthesis' in window)) return;
    // Tapping the message that is currently speaking stops it
    window.speechSynthesis.cancel();
    if (speakingIndex === index) { setSpeakingIndex(null); return; }
    const text = sanitizeForSpeech(content);
    if (!text) { setSpeakingIndex(null); return; }
    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = 'en-AU';
    utt.onend = () => setSpeakingIndex(null);
    utt.onerror = () => setSpeakingIndex(null);
    setSpeakingIndex(index);
    window.speechSynthesis.speak(utt);
  }, [speakingIndex]);

  // Stop any speech when leaving the page
  useEffect(() => () => {
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  }, []);

  // ---- Voice input: big "talk" button. Speech -> live transcript -> send. ----
  const recLang = language === 'fa' ? 'fa-IR' : language === 'ar' ? 'ar-SA' : 'en-AU';
  const startListening = useCallback(() => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { setError('Voice input is not supported on this device. You can type instead.'); return; }
    setError('');
    const rec = new SR();
    rec.continuous = true; rec.interimResults = true; rec.lang = recLang;
    rec.onstart = () => { setListening(true); setVoiceText(''); transcriptRef.current = ''; };
    rec.onresult = (e: any) => {
      let final = '', interim = '';
      for (let i = 0; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
        else interim += e.results[i][0].transcript;
      }
      const t = (final || interim);
      transcriptRef.current = t;
      setVoiceText(t);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    try { rec.start(); } catch { /* already started */ }
  }, [recLang]);

  const stopAndSend = useCallback(() => {
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch {} recognitionRef.current = null; }
    setListening(false);
    const t = transcriptRef.current.trim();
    transcriptRef.current = '';
    setVoiceText('');
    if (t) processQuery(t);
  }, []);

  const toggleTalk = useCallback(() => {
    if (listening) stopAndSend(); else startListening();
  }, [listening, stopAndSend, startListening]);

  // Stop recognition on unmount
  useEffect(() => () => { if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch {} } }, []);

  const firstName = manager?.displayName?.split(' ')[0] || 'there';
  const SERVICES: { label: string; icon: typeof Sun; prompt: string }[] = [
    { label: 'Get briefing', icon: Sun, prompt: 'Give me my morning briefing' },
    { label: 'Add to calendar', icon: CalendarPlus, prompt: 'Add an event to my calendar' },
    { label: 'Follow-up task', icon: ListTodo, prompt: 'Create a follow-up task for me' },
    { label: 'Firm overview', icon: BarChart3, prompt: 'Give me a firm overview' },
  ];

  return (
    <div className="flex flex-col h-full max-w-lg mx-auto overflow-x-hidden">
      {/* Active-client banner + history / new-chat controls */}
      <div className="flex items-center justify-between gap-2 px-2 pt-2">
        {activeClient ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--v4-accent)]/10 px-2.5 py-1 text-xs font-medium text-[var(--v4-accent)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--v4-accent)]" />
            Focused on {activeClient.name}
          </span>
        ) : <span />}
        <div className="flex items-center gap-0.5">
          <button onClick={() => setHistoryOpen(true)} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground transition" aria-label="Chat history">
            <History className="h-4 w-4" />
          </button>
          {messages.length > 0 && (
            <button onClick={newChat} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground transition" aria-label="New chat">
              <Plus className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-2 space-y-4 relative" ref={scrollContainerRef} onScroll={handleScroll}>
        {messages.length === 0 && !isProcessing && (
          <div className="flex flex-col items-center text-center py-6">
            {/* Roni avatar - top middle */}
            <div className="w-20 h-20 rounded-3xl overflow-hidden flex items-center justify-center shadow-lg shadow-[var(--v4-accent)]/25 mb-5">
              <img src="/panel/roni.png" alt="Roni" className="h-full w-full object-cover" />
            </div>
            <h3 className="text-2xl font-light text-foreground">Hi {firstName},</h3>
            <p className="text-base text-muted-foreground mt-1 mb-8">how can I assist you?</p>

            {/* 4 quick service buttons */}
            <div className="grid grid-cols-2 gap-3 w-full max-w-xs mb-10">
              {SERVICES.map(({ label, icon: Icon, prompt }) => (
                <button
                  key={label}
                  onClick={() => processQuery(prompt)}
                  className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-card border border-border hover:border-[var(--v4-accent)]/40 transition active:scale-95"
                >
                  <span className="w-10 h-10 rounded-xl bg-[var(--v4-accent)]/15 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-[var(--v4-accent)]" />
                  </span>
                  <span className="text-xs font-medium text-foreground">{label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={cn(msg.role === 'user' ? 'flex justify-end' : '')}>
            {msg.role === 'assistant' ? (
              <div className="w-full">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-[var(--v4-accent)]/20 flex items-center justify-center">
                      <Sparkles className="h-3 w-3 text-[var(--v4-accent)]" />
                    </div>
                    <span className="text-xs text-muted-foreground">RoniDoc PA</span>
                  </div>
                  <div className="flex items-center gap-0.5">
                    {'speechSynthesis' in window && msg.content && (
                      <button
                        onClick={() => toggleSpeak(i, msg.content)}
                        className={cn(
                          'p-1 rounded-md transition hover:bg-muted',
                          speakingIndex === i ? 'text-[var(--v4-accent)]' : 'text-muted-foreground hover:text-foreground'
                        )}
                        title={speakingIndex === i ? 'Stop' : 'Read aloud'}
                      >
                        {speakingIndex === i ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
                      </button>
                    )}
                    <CopyButton text={msg.content} />
                  </div>
                </div>
                {msg.content && (
                  <div className={cn(
                    'bg-muted/50 border border-border text-foreground rounded-2xl rounded-tl-md px-4 py-3 overflow-hidden',
                    RTL_LANGS.includes(language) && 'direction-rtl'
                  )} style={RTL_LANGS.includes(language) ? { direction: 'rtl', fontFamily: 'Vazir, Vazirmatn, Tahoma, sans-serif' } : undefined}>
                    <FormattedChatMessage content={msg.content} />
                  </div>
                )}

                {/* Success chip for completed actions */}
                {msg.actionDone && (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-green-600 dark:text-green-400">
                    <Check className="h-3.5 w-3.5" /> {msg.actionDone}
                  </div>
                )}

                {/* Confirmation card (action awaiting manager approval) */}
                {msg.confirm && (
                  <ConfirmCard
                    confirm={msg.confirm}
                    state={msg.cardState || 'pending'}
                    onConfirm={() => executeAction(i, msg.confirm!.action, msg.confirm!.params)}
                    onCancel={() => dismissCard(i)}
                  />
                )}
              </div>
            ) : (
              <div className="flex items-start gap-2 max-w-[85%]">
                <div className="bg-[var(--v4-accent)] text-white rounded-2xl rounded-br-md px-4 py-3" style={RTL_LANGS.includes(language) ? { direction: 'rtl', fontFamily: 'Vazir, Vazirmatn, Tahoma, sans-serif' } : undefined}>
                  <p className="text-sm whitespace-pre-wrap">{typeof msg.content === 'string' ? msg.content : String(msg.content)}</p>
                </div>
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0 mt-1">
                  <User className="h-3 w-3 text-muted-foreground" />
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Follow-up suggestions after last assistant message */}
        {messages.length > 0 && messages[messages.length - 1]?.role === 'assistant' && messages[messages.length - 1]?.suggestions && !isProcessing && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Follow up:</p>
            {messages[messages.length - 1].suggestions!.map((s, i) => (
              <button
                key={i}
                onClick={() => processQuery(s)}
                className="w-full text-left text-sm text-muted-foreground hover:text-foreground p-3 rounded-xl bg-muted/50 border border-border hover:border-[var(--v4-accent)]/20 transition"
              >
                {s.replace(/[—–―‒]/g, '-')}
              </button>
            ))}
          </div>
        )}

        {isProcessing && (
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 rounded-full bg-[var(--v4-accent)]/20 flex items-center justify-center">
                <Sparkles className="h-3 w-3 text-[var(--v4-accent)]" />
              </div>
              <span className="text-xs text-muted-foreground">RoniDoc PA</span>
            </div>
            <div className="bg-muted/50 border border-border rounded-2xl rounded-tl-md px-4 py-3">
              <TypingIndicator />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Error */}
      {/* New messages pill */}
      {showScrollPill && (
        <div className="px-2 flex justify-center">
          <button onClick={scrollToBottom} className="flex items-center gap-1.5 px-4 py-1.5 bg-[var(--v4-accent)] text-white text-xs font-medium rounded-full shadow-lg animate-[slideUp_0.2s_ease-out] active:scale-95">
            <ArrowDown className="h-3 w-3" /> New response
          </button>
        </div>
      )}

      {error && (
        <div className="px-4 py-2">
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      {historyOpen && (
        <ChatHistoryPanel
          uid={uid}
          onClose={() => setHistoryOpen(false)}
          onRestore={restoreChat}
          onNewChat={newChat}
        />
      )}
    </div>
  );
}

/**
 * Generate context-aware follow-up suggestions based on the query and answer
 */
function generateSuggestions(query: string, answer: string): string[] {
  const q = query.toLowerCase();
  const a = answer.toLowerCase();

  // Client-specific follow-ups
  if (q.includes('client') && q.includes('attention')) {
    return [
      'Show me their financial details',
      'What deadlines are coming up?',
      'Which clients have declining revenue?',
    ];
  }

  if (q.includes('revenue') || q.includes('profit') || q.includes('income')) {
    return [
      'Which clients are most profitable?',
      'Show expense breakdown by client',
      'Any clients with declining margins?',
      'Compare this year vs last year',
    ];
  }

  if (q.includes('bas') || q.includes('compliance') || q.includes('deadline')) {
    return [
      'Which BAS are overdue?',
      'Show super compliance status',
      'Any audit risk flags?',
      'What penalties are we exposed to?',
    ];
  }

  if (q.includes('tax') || q.includes('deduction') || q.includes('opportunity')) {
    return [
      'Show tax savings by client',
      'What scenarios can reduce tax?',
      'Any unclaimed deductions?',
      'Year-end planning actions needed',
    ];
  }

  if (q.includes('xero') || q.includes('balance') || q.includes('sheet')) {
    return [
      'Show P&L summary for all clients',
      'Any cash flow concerns?',
      'Compare debt ratios across clients',
    ];
  }

  // Generic follow-ups
  return [
    'Tell me more about the top client',
    'Any compliance issues I should know about?',
    'What are the biggest risks right now?',
    'Show me a revenue overview',
  ];
}
